import { IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, TextField } from "@material-ui/core";
import { Call, Send } from "@material-ui/icons";
import React, { useRef } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import { useOrg } from "components/OrgProvider";
import { ContactInfo } from "models/ContactInfo";
import { Nullable } from "models/Nullable";
import { OfferRoom } from "models/OfferRoom";
import { noOrgNameText } from "models/Org";
import { handleEnterKey } from "Util/handleEnterKey";
import { isEmpty } from "Util/isEmpty";
import { ReplaceType } from "Util/ReplaceType";

type Props = {
    offerId: string
    phone?: string
    contactInfo: Nullable<ContactInfo>
    setContactInfo?: (contactInfo: EditableContactInfo) => void
}

export function getOfferTotalTime(offerRooms: OfferRoom[]) {
    return Object
        .values(offerRooms)
        .map(or => Object.values(or.priceFields)
            .map(rpf => (rpf.priceField.expectedTime?.minutes ?? 0) * rpf.count))
        .flat()
        .reduce((acc, val) => acc + val, 0)
}

export function stringIfEmpty(value?: Nullable<string>): string {
    return isEmpty(value) ? '' : value
}

export type EditableContactInfo = Partial<
    ReplaceType<
        ReplaceType<
            ReplaceType<ContactInfo, 'phone', string>,
            'name', Partial<{ firstName: string, lastName: string }>>,
        'email', string>
>


export function OfferContactInfo({ contactInfo, setContactInfo, phone, offerId }: Props) {
    const history = useHistory()
    const [orgDoc] = useOrg()

    const org = orgDoc?.data || null

    const firstNameRef = useRef<any>();
    const lastNameRef = useRef<any>();
    const emailRef = useRef<any>();
    const phoneRef = useRef<any>();

    return (
        setContactInfo ? (
            <List>
                <ListItem>
                    <TextField
                        required
                        variant="outlined"
                        fullWidth label="Fornavn"
                        type="text"
                        inputRef={firstNameRef}
                        autoComplete="off"
                        value={stringIfEmpty(contactInfo?.name?.firstName)}
                        onChange={e => setContactInfo({ name: { firstName: e.target.value } })}
                        onKeyDown={handleEnterKey(() => lastNameRef.current?.focus())}
                    />
                </ListItem>
                <ListItem>
                    <TextField
                        required
                        variant="outlined"
                        fullWidth label="Etternavn"
                        type="text"
                        inputRef={lastNameRef}
                        autoComplete="off"
                        value={stringIfEmpty(contactInfo?.name?.lastName)}
                        onChange={e => setContactInfo({ name: { lastName: e.target.value } })}
                        onKeyDown={handleEnterKey(() => emailRef.current?.focus())}
                    />
                </ListItem>
                <ListItem>
                    <TextField
                        variant="outlined"
                        fullWidth label="E-post"
                        type="email"
                        inputRef={emailRef}
                        autoComplete="off"
                        value={stringIfEmpty(contactInfo?.email?.email)}
                        onChange={e => setContactInfo({ email: e.target.value })}
                        onKeyDown={handleEnterKey(() => phoneRef.current?.focus())}
                    />
                </ListItem>
                <ListItem>
                    <TextField
                        variant="outlined"
                        fullWidth label="Telefonnummer"
                        type="tel"
                        inputRef={phoneRef}
                        autoComplete="off"
                        value={stringIfEmpty(phone)}
                        onChange={e => setContactInfo({ phone: e.target.value })}
                        onKeyDown={handleEnterKey(() => history.goBack())}
                    />
                </ListItem>
            </List>
        ) : (
            <List dense>
                <ListItem component={Link} to={'/anbud/?s=' + encodeURI(contactInfo?.name?.fullName || '')}>
                    <ListItemText primary="Navn" secondary={contactInfo?.name?.fullName} />
                </ListItem>
                {contactInfo?.email?.email ? (
                    <ListItem component={Link} to={'/anbud/?s=' + encodeURI(contactInfo?.email?.email || '')}>
                        <ListItemText primary="E-post" secondary={contactInfo?.email?.email} />
                        <ListItemSecondaryAction>
                            <IconButton
                                component="a"
                                href={`mailto:${contactInfo?.email?.email}?subject=Anbud fra ${String(noOrgNameText(org?.name?.text))} Id<${offerId}>`}>
                                <Send />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ) : null}
                {contactInfo?.phone?.searchStrings.phoneNumber ? (
                    <ListItem component={Link} to={'/anbud/?s=' + encodeURI(String(contactInfo?.phone?.searchStrings.phoneNumber))}>
                        <ListItemText primary="Tlf" secondary={contactInfo?.phone?.phoneNumberString} />
                        <ListItemSecondaryAction>
                            <IconButton
                                component="a"
                                href={`tel:${contactInfo?.phone?.phoneNumberString}`}>
                                <Call />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ) : null}
            </List>
        )
    )
}